'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { useMenu as useMenuProvider } from '@/providers/menu-provider';
import { useMenu } from '@/hooks/use-menu';
import { ChevronRight, ChevronDown } from 'lucide-react';

// Badge Komponente
function MenuBadge({ text, variant = 'default' }) {
  const variants = {
    default: 'bg-primary/10 text-primary',
    soon: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
    new: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
    beta: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
  };

  // Auto-detect variant from text
  const autoVariant = text?.toLowerCase() === 'soon' ? 'soon'
    : text?.toLowerCase() === 'new' || text?.toLowerCase() === 'neu' ? 'new'
    : text?.toLowerCase() === 'beta' ? 'beta'
    : 'default';

  return (
    <span className={cn(
      "ml-2 px-1.5 py-0.5 text-[10px] font-medium rounded uppercase tracking-wide",
      variants[variant] || variants[autoVariant]
    )}>
      {text}
    </span>
  );
}

// Rekursives Flyout-Submenü (Windows-Stil) mit Click-Support
function FlyoutSubmenu({ items, level = 0, openPath, setOpenPath, pathPrefix = '', isActive, hasActiveChild }) {
  if (!items || items.length === 0) return null;

  const handleItemClick = (idx, hasChildren, isDisabled, e) => {
    if (isDisabled) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (hasChildren) {
      e.preventDefault();
      e.stopPropagation();
      const newPath = `${pathPrefix}${idx}`;
      // Toggle: wenn bereits offen, schließen; sonst öffnen
      if (openPath.startsWith(newPath)) {
        // Schließe dieses und alle Kinder
        setOpenPath(pathPrefix.slice(0, -1));
      } else {
        setOpenPath(newPath);
      }
    }
  };

  return (
    <ul className="min-w-[220px] py-1 overflow-visible">
      {items.map((item, idx) => {
        // Divider vor dem Item
        const divider = item.divider_before ? (
          <li key={`flyout-divider-${level}-${idx}`} className="my-2 mx-3 border-t-2 border-muted-foreground/40" />
        ) : null;

        // Überschrift / Header
        if (item.heading || item.type === 'header') {
          return (
            <>
              {divider}
              <li key={`flyout-header-${level}-${idx}`} className="px-3 pt-3 pb-1">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {item.heading}
                </span>
              </li>
            </>
          );
        }

        const hasChildren = item.children && item.children.length > 0;
        const itemPath = `${pathPrefix}${idx}`;
        const isDirectlyOpen = openPath === itemPath || openPath.startsWith(`${itemPath}-`);
        const isDisabled = item.disabled === true;
        const IconComponent = item.icon;
        // Prüfe ob dieses Item oder ein Kind aktiv ist
        const itemIsActive = typeof isActive === 'function' && item.path && isActive(item.path);
        const itemHasActiveChild = typeof hasActiveChild === 'function' && hasChildren && hasActiveChild(item.children);
        const showActiveStyle = itemIsActive || itemHasActiveChild;

        return (
          <>
            {divider}
            <li
              key={`flyout-${level}-${idx}`}
              className="relative overflow-visible"
            >
            {hasChildren ? (
              // Hat Kinder - zeigt Pfeil und Submenu bei Klick
              <>
                {/* Trigger */}
                <div
                  className={cn(
                    "flex items-center justify-between px-3 py-2 text-sm cursor-pointer select-none",
                    isDirectlyOpen && !showActiveStyle && "bg-accent",
                    !isDirectlyOpen && !showActiveStyle && "hover:bg-accent",
                    showActiveStyle && "text-primary font-medium bg-primary/10",
                    isDisabled && "opacity-50 cursor-not-allowed"
                  )}
                  onClick={(e) => handleItemClick(idx, true, isDisabled, e)}
                >
                  <div className="flex items-center flex-1 min-w-0">
                    {IconComponent && (
                      <span className={cn("mr-2 flex-shrink-0", showActiveStyle ? "text-primary" : "text-muted-foreground")}>
                        <IconComponent className="h-4 w-4" />
                      </span>
                    )}
                    {item.path && !isDisabled ? (
                      <Link
                        href={item.path}
                        className={cn("flex-1 truncate", showActiveStyle ? "text-primary" : "hover:text-primary")}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {item.title}
                      </Link>
                    ) : (
                      <span className={cn("flex-1 truncate", showActiveStyle && "text-primary")}>{item.title}</span>
                    )}
                    {item.badge_text && <MenuBadge text={item.badge_text} />}
                    {isDisabled && !item.badge_text && <MenuBadge text="Soon" variant="soon" />}
                  </div>
                  <ChevronRight className={cn(
                    "h-4 w-4 ml-2 text-muted-foreground transition-transform flex-shrink-0",
                    isDirectlyOpen && "rotate-90"
                  )} />
                </div>
                {/* Submenu - erscheint NUR bei Klick mit Animation */}
                {isDirectlyOpen && (
                  <div
                    className="absolute left-full top-0 animate-in fade-in-0 zoom-in-95 slide-in-from-left-2 duration-150"
                    style={{ zIndex: 9999 + level }}
                  >
                    <div className="ml-1 bg-popover border rounded-md shadow-lg overflow-visible">
                      <FlyoutSubmenu
                        items={item.children}
                        level={level + 1}
                        openPath={openPath}
                        setOpenPath={setOpenPath}
                        pathPrefix={`${itemPath}-`}
                        isActive={isActive}
                        hasActiveChild={hasActiveChild}
                      />
                    </div>
                  </div>
                )}
              </>
            ) : (
              // Leaf Node - einfacher Link oder disabled
              isDisabled ? (
                <div className="flex items-center px-3 py-2 text-sm opacity-50 cursor-not-allowed">
                  {IconComponent && (
                    <span className="mr-2 text-muted-foreground">
                      <IconComponent className="h-4 w-4" />
                    </span>
                  )}
                  <span className="flex-1 truncate">{item.title}</span>
                  {item.badge_text && <MenuBadge text={item.badge_text} />}
                  {!item.badge_text && <MenuBadge text="Soon" variant="soon" />}
                </div>
              ) : (
                <Link
                  href={item.path || '#'}
                  className={cn(
                    "flex items-center px-3 py-2 text-sm",
                    showActiveStyle ? "text-primary font-medium bg-primary/10" : "hover:bg-accent hover:text-primary"
                  )}
                >
                  {IconComponent && (
                    <span className={cn("mr-2", showActiveStyle ? "text-primary" : "text-muted-foreground")}>
                      <IconComponent className="h-4 w-4" />
                    </span>
                  )}
                  <span className="flex-1 truncate">{item.title}</span>
                  {item.badge_text && <MenuBadge text={item.badge_text} />}
                </Link>
              )
            )}
          </li>
          </>
        );
      })}
    </ul>
  );
}

// Click-Only Menu Item (kein Hover)
function ClickMenuItem({ item, idx, isActive, hasActiveChild }) {
  const [isOpen, setIsOpen] = useState(false);
  const [openPath, setOpenPath] = useState('');
  const menuRef = useRef(null);
  const pathname = usePathname();

  // Click-Outside Handler
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsOpen(false);
        setOpenPath('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Schließen bei Navigation
  useEffect(() => {
    setIsOpen(false);
    setOpenPath('');
  }, [pathname]);

  const hasChildren = Array.isArray(item.children) && item.children.length > 0;
  const isDisabled = item.disabled === true;
  const IconComponent = item.icon;

  const linkClass = cn(
    "inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md cursor-pointer select-none",
    "text-secondary-foreground hover:text-primary",
    isOpen && "text-primary bg-accent/50",
    hasActiveChild(item.children || []) && "text-primary",
    isDisabled && "opacity-50 cursor-not-allowed"
  );

  // Überschrift (nur Text, kein Link)
  if (item.heading || item.type === 'header') {
    return (
      <div className="px-4 py-2">
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          {item.heading}
        </span>
      </div>
    );
  }

  if (!hasChildren) {
    // Einfacher Link ohne Dropdown
    if (isDisabled) {
      return (
        <div className="relative">
          <span className={linkClass}>
            {IconComponent && <IconComponent className="h-4 w-4" />}
            {item.title}
            {item.badge_text && <MenuBadge text={item.badge_text} />}
            {!item.badge_text && <MenuBadge text="Soon" variant="soon" />}
          </span>
        </div>
      );
    }
    return (
      <div className="relative">
        <Link href={item.path || '#'} className={linkClass}>
          {IconComponent && <IconComponent className="h-4 w-4" />}
          {item.title}
          {item.badge_text && <MenuBadge text={item.badge_text} />}
        </Link>
      </div>
    );
  }

  return (
    <div className="relative" ref={menuRef}>
      {/* Trigger - NUR Klick, kein Hover */}
      <button
        className={linkClass}
        onClick={() => {
          if (!isDisabled) {
            setIsOpen(!isOpen);
            setOpenPath('');
          }
        }}
        disabled={isDisabled}
      >
        {IconComponent && <IconComponent className="h-4 w-4" />}
        {item.title}
        {item.badge_text && <MenuBadge text={item.badge_text} />}
        {isDisabled && !item.badge_text && <MenuBadge text="Soon" variant="soon" />}
        <ChevronDown className={cn(
          "h-3.5 w-3.5 opacity-60 transition-transform duration-200",
          isOpen && "rotate-180"
        )} />
      </button>

      {/* Dropdown Content mit Animation */}
      {isOpen && (
        <div
          className="absolute top-full left-0 mt-1.5 bg-popover border rounded-md shadow-lg overflow-visible
                     animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 duration-200"
          style={{ zIndex: 9999 }}
        >
          <FlyoutSubmenu
            items={item.children}
            openPath={openPath}
            setOpenPath={setOpenPath}
            isActive={isActive}
            hasActiveChild={hasActiveChild}
          />
        </div>
      )}
    </div>
  );
}

export function MegaMenu() {
  const pathname = usePathname();
  const { isActive, hasActiveChild } = useMenu(pathname);
  const { headerMenu, isLoading } = useMenuProvider();

  const currentMenu = headerMenu || [];

  if (!currentMenu || currentMenu.length === 0) {
    return <div className="flex-1" />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center space-x-4">
        <div className="text-sm text-muted-foreground">Lade Header-Menü...</div>
      </div>
    );
  }

  return (
    <nav className="flex-1 flex items-center gap-1">
      {currentMenu.map((item, idx) => (
        <ClickMenuItem
          key={`menu-${idx}`}
          item={item}
          idx={idx}
          isActive={isActive}
          hasActiveChild={hasActiveChild}
        />
      ))}
    </nav>
  );
}
